import React, { useState } from 'react';
import { DashboardPageHeader, dashboardCardClass, dashboardInputClass, dashboardSelectClass } from './DashboardChrome';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, PointElement, LineElement, Title } from 'chart.js';
import { Pie, Line } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, PointElement, LineElement, Title, ChartDataLabels);

const AnalyticsSection = ({ projects, expenses, quotes }) => {
  const [selectedProject, setSelectedProject] = useState('all');
  const [dateRange, setDateRange] = useState('currentmonth');
  const [projectSearchTerm, setProjectSearchTerm] = useState('');
  const [revenueChartYear, setRevenueChartYear] = useState(new Date().getFullYear());

  // Calculate analytics data based on selected filters
  const getAnalyticsData = () => {
    const now = new Date();
    let startDate = new Date();
    
    // Set date range based on selection
    switch (dateRange) {
      case 'currentmonth':
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        break;
      case 'last3months':
        startDate.setMonth(now.getMonth() - 3);
        break;
      case 'last6months':
        startDate.setMonth(now.getMonth() - 6);
        break;
      case 'last12months':
        startDate.setMonth(now.getMonth() - 12);
        break;
      case 'thisyear':
        startDate = new Date(now.getFullYear(), 0, 1);
        break;
      case 'alltime':
        startDate = new Date(0); // Beginning of time
        break;
      default:
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
    }

    // Filter projects and expenses based on date range and selected project
    const filteredProjects = projects.filter(project => {
      const projectDate = new Date(project.createdAt || project.startDate);
      const matchesDate = projectDate >= startDate && projectDate <= now;
      const matchesProject = selectedProject === 'all' || project.id === selectedProject;
      return matchesDate && matchesProject;
    });

    const filteredExpenses = expenses.filter(expense => {
      const expenseDate = new Date(expense.date);
      const matchesDate = expenseDate >= startDate && expenseDate <= now;
      const matchesProject = selectedProject === 'all' || expense.projectId === selectedProject;
      
      // Only include expenses for projects that still exist
      const projectExists = !expense.projectId || projects.some(p => p.id === expense.projectId);
      
      return matchesDate && matchesProject && projectExists;
    });

    const filteredQuotes = quotes.filter(quote => {
      const quoteDate = new Date(quote.createdAt);
      const matchesDate = quoteDate >= startDate && quoteDate <= now;
      
      // Only include quotes that are either standalone or linked to existing projects
      const isLinkedToExistingProject = projects.some(project => project.selectedQuoteId === quote.id);
      const isStandalone = !isLinkedToExistingProject;
      
      return matchesDate && (isStandalone || isLinkedToExistingProject);
    });

    return { filteredProjects, filteredExpenses, filteredQuotes };
  };

  const { filteredProjects, filteredExpenses, filteredQuotes } = getAnalyticsData();

  // Filter projects based on search term
  const filteredProjectsForSearch = projects.filter(project => 
    project.name.toLowerCase().includes(projectSearchTerm.toLowerCase())
  );

  // Calculate comprehensive project metrics
  const getProjectMetrics = () => {
    const now = new Date();
    const totalProjects = filteredProjects.length;
    const completedProjects = filteredProjects.filter(p => p.status === 'completed' || (p.deadline && new Date(p.deadline) < now)).length;
    const ongoingProjects = filteredProjects.filter(p => p.status === 'ongoing' || (p.startDate && p.deadline && new Date(p.startDate) <= now && new Date(p.deadline) >= now)).length;
    const upcomingProjects = totalProjects - completedProjects - ongoingProjects;
    const completionRate = totalProjects > 0 ? Math.round((completedProjects / totalProjects) * 100) : 0;

    return {
      total: totalProjects,
      completed: completedProjects,
      ongoing: ongoingProjects,
      upcoming: upcomingProjects,
      completionRate
    };
  };

  // Calculate comprehensive financial metrics
  const getFinancialMetrics = () => {
    // Calculate revenue from projects with linked quotes
    const projectsWithQuotes = filteredProjects.filter(project => project.selectedQuoteId);

    const totalRevenue = projectsWithQuotes.reduce((sum, project) => {
      const linkedQuote = quotes.find(quote => quote.id === project.selectedQuoteId);
      const revenue = parseFloat(linkedQuote?.agreedPrice) || 0;
      return sum + revenue;
    }, 0);

    // Also include revenue from standalone quotes (for backward compatibility)
    const standaloneQuotes = filteredQuotes.filter(quote => 
      quote.agreedPrice && !projectsWithQuotes.some(project => project.selectedQuoteId === quote.id) && 
      !projects.some(project => project.selectedQuoteId === quote.id) // Ensure quote is not linked to any existing project
    );
    const standaloneRevenue = standaloneQuotes.reduce((sum, quote) => sum + (parseFloat(quote.agreedPrice) || 0), 0);
    const totalRevenueWithStandalone = totalRevenue + standaloneRevenue;

    // Calculate total expenses from project-specific expenses only
    const projectExpenses = filteredExpenses.filter(exp => exp.projectId);
    const totalExpenses = projectExpenses.reduce((sum, exp) => sum + parseFloat(exp.amount || 0), 0);
    
    // Add automatic labour costs from quotes for projects
    let totalLabourFromQuotes = 0;
    projectsWithQuotes.forEach(project => {
      const linkedQuote = quotes.find(quote => quote.id === project.selectedQuoteId);
      if (linkedQuote && linkedQuote.labourCost) {
        totalLabourFromQuotes += parseFloat(linkedQuote.labourCost) || 0;
      }
    });
    
    const totalExpensesWithLabour = totalExpenses + totalLabourFromQuotes;

    // Calculate profit and margins
    const totalProfit = totalRevenueWithStandalone - totalExpensesWithLabour;
    const profitMargin = totalRevenueWithStandalone > 0 ? Math.round((totalProfit / totalRevenueWithStandalone) * 100) : 0;
    const averageProfitPerJob = filteredProjects.length > 0 ? totalProfit / filteredProjects.length : 0;

    // Calculate markup analysis
    const quotesWithMarkup = filteredQuotes.filter(quote => quote.markup);
    const averageMarkup = quotesWithMarkup.length > 0 
      ? quotesWithMarkup.reduce((sum, quote) => sum + (parseFloat(quote.markup) || 0), 0) / quotesWithMarkup.length 
      : 0;

    // Calculate overhead analysis
    const overheadExpenses = filteredExpenses.filter(exp => 
      exp.category === 'overhead' || 
      exp.category === 'office' || 
      exp.category === 'administrative' ||
      exp.category === 'insurance' ||
      exp.category === 'utilities'
    );
    const totalOverhead = overheadExpenses.reduce((sum, exp) => sum + parseFloat(exp.amount || 0), 0);
    const overheadPercentage = totalExpenses > 0 ? Math.round((totalOverhead / totalExpenses) * 100) : 0;

    // Calculate budget vs actual
    const projectsWithBudget = filteredProjects.filter(p => p.budget);
    const totalEstimatedBudget = projectsWithBudget.reduce((sum, p) => sum + (parseFloat(p.budget) || 0), 0);
    const budgetVariance = totalEstimatedBudget - totalExpenses;
    const budgetAccuracy = totalEstimatedBudget > 0 ? Math.round(((totalEstimatedBudget - Math.abs(budgetVariance)) / totalEstimatedBudget) * 100) : 0;

    // Category breakdown
    const categoryBreakdown = {};
    filteredExpenses.forEach(expense => {
      const category = expense.category || 'other';
      categoryBreakdown[category] = (categoryBreakdown[category] || 0) + parseFloat(expense.amount || 0);
    });
    
    // Add labour costs from quotes to the category breakdown
    projectsWithQuotes.forEach(project => {
      const linkedQuote = quotes.find(quote => quote.id === project.selectedQuoteId);
      if (linkedQuote && linkedQuote.labourCost) {
        const labourAmount = parseFloat(linkedQuote.labourCost) || 0;
        categoryBreakdown['labour'] = (categoryBreakdown['labour'] || 0) + labourAmount;
      }
    });

    return {
      totalExpenses: totalExpensesWithLabour,
      totalRevenue: totalRevenueWithStandalone,
      totalProfit,
      profitMargin,
      averageProfitPerJob,
      averageMarkup,
      totalOverhead,
      overheadPercentage,
      totalEstimatedBudget,
      budgetVariance,
      budgetAccuracy,
      categoryBreakdown
    };
  };

  // Calculate project performance analysis
  const getProjectPerformance = () => {
    return filteredProjects.map(project => {
      const projectExpenses = filteredExpenses.filter(exp => exp.projectId === project.id);
      const actualExpenses = projectExpenses.reduce((sum, exp) => sum + parseFloat(exp.amount), 0);
      
      // Add labour cost from linked quote if it exists
      let labourCostFromQuote = 0;
      if (project.selectedQuoteId) {
        const linkedQuote = quotes.find(quote => quote.id === project.selectedQuoteId);
        if (linkedQuote && linkedQuote.labourCost) {
          labourCostFromQuote = parseFloat(linkedQuote.labourCost) || 0;
        }
      }
      
      const actualExpensesWithLabour = actualExpenses + labourCostFromQuote;
      const estimatedBudget = parseFloat(project.budget) || 0;
      
      // Find final price for this project using linked quote
      let finalQuotePrice = 0;
      if (project.selectedQuoteId) {
        const linkedQuote = quotes.find(quote => quote.id === project.selectedQuoteId);
        finalQuotePrice = parseFloat(linkedQuote?.agreedPrice) || 0;
      } else {
        // Fallback to searching by project name (for backward compatibility)
      const projectQuotes = quotes.filter(quote => 
        quote.agreedPrice && quote.description && 
        quote.description.toLowerCase().includes(project.name.toLowerCase())
      );
        finalQuotePrice = projectQuotes.reduce((sum, quote) => sum + (parseFloat(quote.agreedPrice) || 0), 0);
      }
      
      const budgetVariance = estimatedBudget - actualExpensesWithLabour;
      const profit = finalQuotePrice - actualExpensesWithLabour;
      const profitMargin = finalQuotePrice > 0 ? (profit / finalQuotePrice) * 100 : 0;
      
      return {
        projectName: project.name,
        estimatedBudget,
        actualExpenses: actualExpensesWithLabour,
        finalQuotePrice,
        budgetVariance,
        profit,
        profitMargin,
        status: budgetVariance >= 0 ? 'under' : 'over',
        profitStatus: profit >= 0 ? 'profitable' : 'loss',
        hasLinkedQuote: !!project.selectedQuoteId
      };
    });
  };

  // Generate chart data
  const getChartData = () => {
    const financial = getFinancialMetrics();
    
    // Category name mapping for display
    const categoryDisplayNames = {
      'materials': 'Materials',
      'labor': 'Labour',
      'equipment': 'Equipment',
      'transportation': 'Transportation',
      'utilities': 'Utilities',
      'permits': 'Permits',
      'other': 'Other'
    };
    
    // Pie chart for expense categories
    const pieData = {
      labels: Object.keys(financial.categoryBreakdown).map(category => 
        categoryDisplayNames[category] || category.charAt(0).toUpperCase() + category.slice(1)
      ),
      datasets: [{
        data: Object.values(financial.categoryBreakdown),
        backgroundColor: [
          '#3B82F6', // Blue
          '#10B981', // Green
          '#F59E0B', // Orange
          '#EF4444', // Red
          '#8B5CF6', // Purple
          '#06B6D4', // Cyan
          '#F97316', // Amber
          '#84CC16', // Lime
        ],
        borderWidth: 2,
        borderColor: '#fff'
      }]
    };

    return { pieData };
  };

  // Generate revenue line chart data
  const getRevenueChartData = () => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const monthlyRevenue = new Array(12).fill(0);

    // Get all projects for the selected year
    const yearProjects = projects.filter(project => {
      const projectDate = new Date(project.createdAt || project.startDate);
      return projectDate.getFullYear() === revenueChartYear && project.selectedQuoteId;
    });

    // Calculate monthly revenue
    yearProjects.forEach(project => {
      const projectDate = new Date(project.createdAt || project.startDate);
      const month = projectDate.getMonth();
      const linkedQuote = quotes.find(quote => quote.id === project.selectedQuoteId);
      const revenue = parseFloat(linkedQuote?.agreedPrice) || 0;
      monthlyRevenue[month] += revenue;
    });

    const lineData = {
      labels: months,
      datasets: [{
        label: 'Monthly Revenue',
        data: monthlyRevenue,
        borderColor: '#3B82F6',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        borderWidth: 3,
        fill: true,
        tension: 0.4,
        pointBackgroundColor: '#3B82F6',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointRadius: 6,
        pointHoverRadius: 8
      }]
    };

    return lineData;
  };

  const projectMetrics = getProjectMetrics();
  const financialMetrics = getFinancialMetrics();
  const projectPerformance = getProjectPerformance();
  const { pieData } = getChartData();

  return (
    <div className="mx-auto max-w-7xl space-y-8 pb-10">
      <DashboardPageHeader
        eyebrow="Pro · Analytics"
        title="Contractor analytics"
        description="Filter by time range and project to see revenue, costs, and performance at a glance."
        action={
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className={`${dashboardSelectClass} min-w-[200px]`}
          >
            <option value="currentmonth">This month</option>
            <option value="last3months">Last 3 months</option>
            <option value="last6months">Last 6 months</option>
            <option value="last12months">Last 12 months</option>
            <option value="thisyear">This year</option>
            <option value="alltime">All time</option>
          </select>
        }
      />

      <div className={`${dashboardCardClass} p-6`}>
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-slate-900">Project filter</h2>
          <button
            type="button"
            onClick={() => setSelectedProject('all')}
            className={`rounded-xl px-4 py-2 text-sm font-semibold transition-colors ${
              selectedProject === 'all'
                ? 'bg-primary text-white shadow-md shadow-primary/20'
                : 'border border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
            }`}
          >
            All projects
          </button>
        </div>
        <div className="relative">
          <input
            type="text"
            placeholder="Search projects…"
            value={projectSearchTerm}
            onChange={(e) => setProjectSearchTerm(e.target.value)}
            className={dashboardInputClass}
          />
              {projectSearchTerm && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {filteredProjectsForSearch.map(project => (
                    <button
                      key={project.id}
                      onClick={() => {
                        setSelectedProject(project.id);
                        setProjectSearchTerm(project.name);
                        // Clear the search term to hide the dropdown
                        setTimeout(() => setProjectSearchTerm(''), 100);
                      }}
                      className="w-full px-4 py-3 text-left hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                    >
                      <div className="font-medium text-gray-900">{project.name}</div>
                      <div className="text-sm text-gray-500">Budget: ${parseFloat(project.budget || 0).toLocaleString()}</div>
                    </button>
                  ))}
                </div>
              )}
        </div>
      </div>

        {/* Key Financial Metrics - Only show when no specific project is selected */}
        {selectedProject === 'all' && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <div className="bg-white rounded-lg border border-gray-200 p-6 text-center">
                <div className="text-3xl font-semibold text-gray-900 mb-1">
                  ${financialMetrics.totalProfit.toLocaleString()}
                </div>
                <div className="text-sm font-medium text-gray-600 uppercase tracking-wide">
                  Total Profit
                </div>
              </div>

              <div className="bg-white rounded-lg border border-gray-200 p-6 text-center">
                <div className="text-3xl font-semibold text-gray-900 mb-1">
                  ${financialMetrics.totalRevenue.toLocaleString()}
                </div>
                <div className="text-sm font-medium text-gray-600 uppercase tracking-wide">
                  Total Revenue
                </div>
              </div>

              <div className="bg-white rounded-lg border border-gray-200 p-6 text-center">
                <div className="text-3xl font-semibold text-gray-900 mb-1">
                  ${financialMetrics.totalExpenses.toLocaleString()}
                </div>
                <div className="text-sm font-medium text-gray-600 uppercase tracking-wide">
                  Total Expenses
                </div>
              </div>

              <div className="bg-white rounded-lg border border-gray-200 p-6 text-center">
                <div className="text-3xl font-semibold text-gray-900 mb-1">
                  ${financialMetrics.averageProfitPerJob.toLocaleString()}
                </div>
                <div className="text-sm font-medium text-gray-600 uppercase tracking-wide">
                  Avg. Profit/Project
                </div>
              </div>
            </div>

            {/* Project Status Metrics - Only show when no specific project is selected */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="bg-white rounded-lg border border-gray-200 p-6 text-center">
                <div className="text-3xl font-semibold text-gray-900 mb-1">
                  {projectMetrics.total}
                </div>
                <div className="text-sm font-medium text-gray-600 uppercase tracking-wide">
                  Total Projects
                </div>
              </div>

              <div className="bg-white rounded-lg border border-gray-200 p-6 text-center">
                <div className="text-3xl font-semibold text-gray-900 mb-1">
                  {projectMetrics.completed}
                </div>
                <div className="text-sm font-medium text-gray-600 uppercase tracking-wide">
                  Completed Projects
                </div>
              </div>

              <div className="bg-white rounded-lg border border-gray-200 p-6 text-center">
                <div className="text-3xl font-semibold text-gray-900 mb-1">
                  {projectMetrics.ongoing}
                </div>
                <div className="text-sm font-medium text-gray-600 uppercase tracking-wide">
                  Ongoing Projects
                </div>
              </div>
            </div>
          </>
        )}

        {/* Charts Section - Side by Side for All Projects */}
        {selectedProject === 'all' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
            {/* Revenue Trend Chart */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Revenue Trend</h3>
                <div className="flex items-center space-x-2">
                  <label className="text-sm font-medium text-gray-700">Year:</label>
                  <select
                    value={revenueChartYear}
                    onChange={(e) => setRevenueChartYear(parseInt(e.target.value))}
                    className="px-3 py-1 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-sm"
                  >
                    {Array.from(new Set(projects.map(project => {
                      const projectDate = new Date(project.createdAt || project.startDate);
                      return projectDate.getFullYear();
                    }))).sort((a, b) => b - a).map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="h-80">
                <Line 
                  data={getRevenueChartData()} 
                  options={{ 
                    maintainAspectRatio: false,
                    responsive: true,
                    plugins: {
                      legend: {
                        display: false
                      },
                      tooltip: {
                        callbacks: {
                          label: function(context) {
                            return `Revenue: $${context.parsed.y.toLocaleString()}`;
                          }
                        }
                      },
                      datalabels: {
                        display: false
                      }
                    },
                    scales: {
                      y: {
                        beginAtZero: true,
                        ticks: {
                          callback: function(value) {
                            return '$' + value.toLocaleString();
                          }
                        },
                        grid: {
                          color: 'rgba(0, 0, 0, 0.1)'
                        }
                      },
                      x: {
                        grid: {
                          color: 'rgba(0, 0, 0, 0.1)'
                        }
                      }
                    },
                    elements: {
                      point: {
                        hoverRadius: 8
                      }
                    }
                  }} 
                />
              </div>
            </div>

            {/* Expense Breakdown Chart */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Expense Breakdown</h3>
              <div className="flex justify-center">
                <div className="w-80 h-60">
                  <Pie 
                    data={pieData} 
                    options={{ 
                      maintainAspectRatio: false,
                                          plugins: {
                      legend: {
                        position: 'bottom',
                        align: 'center',
                        margin: 20,
                        labels: {
                          boxWidth: 12,
                          padding: 12,
                          font: {
                            size: 11
                          },
                          generateLabels: function(chart) {
                            const data = chart.data;
                            if (data.labels.length && data.datasets.length) {
                              return data.labels.map(function(label, i) {
                                const value = data.datasets[0].data[i];
                                const total = data.datasets[0].data.reduce((a, b) => a + b, 0);
                                const percentage = ((value / total) * 100).toFixed(1);
                                return {
                                  text: `${label}: $${value.toLocaleString()} (${percentage}%)`,
                                  fillStyle: data.datasets[0].backgroundColor[i],
                                  strokeStyle: data.datasets[0].backgroundColor[i],
                                  lineWidth: 0,
                                  hidden: false,
                                  index: i
                                };
                              });
                            }
                            return [];
                          }
                        }
                      },
                        tooltip: {
                          callbacks: {
                            label: function(context) {
                              const label = context.label || '';
                              const value = context.parsed;
                              const total = context.dataset.data.reduce((a, b) => a + b, 0);
                              const percentage = ((value / total) * 100).toFixed(1);
                              return `${label}: $${value.toLocaleString()} (${percentage}%)`;
                            }
                          }
                        },
                        datalabels: {
                          display: false
                        }
                      }
                    }} 
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Budget vs Actual Analysis */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-8">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Budget vs Actual Analysis</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Project</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Estimated Budget</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Expenses</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Invoice total</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Budget Variance</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Profit</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Profit Margin</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {projectPerformance.map((item, index) => {
                  const budgetUtilization = item.estimatedBudget > 0 ? (item.actualExpenses / item.estimatedBudget) * 100 : 0;
                  let statusColor = 'bg-green-100 text-green-800';
                  let statusText = 'On Track';
                  
                  if (item.status === 'over') {
                    statusColor = 'bg-red-100 text-red-800';
                    statusText = 'Over Budget';
                  } else if (budgetUtilization >= 85) {
                    statusColor = 'bg-yellow-100 text-yellow-800';
                    statusText = 'At Risk';
                  }
                  
                  return (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        <div className="flex items-center">
                          {item.projectName}
                          {item.hasLinkedQuote && (
                            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" title="Linked to invoice">
                              <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                              </svg>
                              Invoice
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">${item.estimatedBudget.toLocaleString()}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">${item.actualExpenses.toLocaleString()}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">${item.finalQuotePrice.toLocaleString()}</td>
                      <td className={`px-6 py-4 whitespace-nowrap text-sm text-right font-medium ${
                        item.status === 'under' ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {item.status === 'under' ? '+' : '-'}${Math.abs(item.budgetVariance).toLocaleString()}
                      </td>
                      <td className={`px-6 py-4 whitespace-nowrap text-sm text-right font-medium ${
                        item.profitStatus === 'profitable' ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {item.profitStatus === 'profitable' ? '+' : '-'}${Math.abs(item.profit).toLocaleString()}
                      </td>
                      <td className={`px-6 py-4 whitespace-nowrap text-sm text-right font-medium ${
                        item.profitMargin >= 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {item.profitMargin.toFixed(1)}%
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusColor}`}>
                          {statusText}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Expense Breakdown Chart - Only show when specific project is selected */}
        {selectedProject !== 'all' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Expense Breakdown</h3>
            <div className="flex justify-center">
              <div className="w-96 h-72">
                <Pie 
                  data={pieData} 
                  options={{ 
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: 'bottom',
                        align: 'center',
                        margin: 20,
                        labels: {
                          boxWidth: 12,
                          padding: 12,
                          font: {
                            size: 11
                          },
                          generateLabels: function(chart) {
                            const data = chart.data;
                            if (data.labels.length && data.datasets.length) {
                              return data.labels.map(function(label, i) {
                                const value = data.datasets[0].data[i];
                                const total = data.datasets[0].data.reduce((a, b) => a + b, 0);
                                const percentage = ((value / total) * 100).toFixed(1);
                                return {
                                  text: `${label}: $${value.toLocaleString()} (${percentage}%)`,
                                  fillStyle: data.datasets[0].backgroundColor[i],
                                  strokeStyle: data.datasets[0].backgroundColor[i],
                                  lineWidth: 0,
                                  hidden: false,
                                  index: i
                                };
                              });
                            }
                            return [];
                          }
                        }
                      },
                      tooltip: {
                        callbacks: {
                          label: function(context) {
                            const label = context.label || '';
                            const value = context.parsed;
                            const total = context.dataset.data.reduce((a, b) => a + b, 0);
                            const percentage = ((value / total) * 100).toFixed(1);
                            return `${label}: $${value.toLocaleString()} (${percentage}%)`;
                          }
                        }
                      },
                      datalabels: {
                        display: false
                      }
                    }
                  }} 
                />
              </div>
            </div>
          </div>
        )}
    </div>
  );
};

export default AnalyticsSection; 